(ns capra.lifecycle
  "Client-side stub and callback lifecycle management.

   Tracks ownership: which callbacks belong to which stubs,
   which stubs are children of other stubs. Dispose cascades."
  (:require
   [capra.callback :as callback]
   [capra.stub :as stub]))

;; cap-id → #{fn-id ...} — callbacks owned by this stub
(defonce ^:private owned-callbacks (atom {}))

;; cap-id → #{child-cap-id ...} — stubs returned from calling this stub
(defonce ^:private child-stubs (atom {}))

;; All live stubs — cap-id → CapraStub
(defonce ^:private live-stubs (atom {}))

(defn register-stub!
  "Track a live stub."
  [stub]
  (swap! live-stubs assoc (:cap-id stub) stub))

(defn own-callback!
  "Associate a callback with a parent stub. Released when stub is disposed."
  [parent-stub capra-callback]
  (swap! owned-callbacks update (:cap-id parent-stub)
         (fnil conj #{}) (:fn-id capra-callback)))

(defn register-child!
  "Record that child-stub was obtained by calling parent-stub."
  [parent-stub child-stub]
  (swap! child-stubs update (:cap-id parent-stub)
         (fnil conj #{}) (:cap-id child-stub)))

(defn dispose!
  "Release a stub: sends release to server, drops owned callbacks and children.
   transport-release-fn is (fn [cap-ids fn-ids] ...) — sends :rpc/release."
  [stub transport-release-fn]
  (let [cap-id (:cap-id stub)
        fns    (get @owned-callbacks cap-id #{})
        children (get @child-stubs cap-id #{})]
    ;; Release owned callbacks
    (doseq [fn-id fns]
      (callback/release! fn-id))
    ;; Recursively dispose children
    (doseq [child-id children]
      (when-let [child (get @live-stubs child-id)]
        (dispose! child transport-release-fn)))
    ;; Clean up tracking
    (swap! owned-callbacks dissoc cap-id)
    (swap! child-stubs dissoc cap-id)
    (swap! live-stubs dissoc cap-id)
    ;; Tell the server
    (transport-release-fn #{cap-id} fns)))

(defn live-stub-ids
  "Return the set of all live stub cap-ids."
  []
  (set (keys @live-stubs)))

(defn clear-all!
  "Clear all lifecycle state. Used on disconnect."
  []
  (reset! owned-callbacks {})
  (reset! child-stubs {})
  (reset! live-stubs {}))
